import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Stack, Typography} from '@mui/material';

import {supplierDetailsQuery} from '@store/suppliers/details';
import {UnitOfMeasure} from '@store/settings/serviceKinds';
import {ManagePrice} from '@store/common/price.model';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import ModalComponent from '@components/modal/Modal.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import SelectComponent from '@components/inputs/select/Select.component';

interface ManagePriceModalProps {
  handleClose: () => void;
  handleSaved: (price: ManagePrice) => void;
  price?: ManagePrice;
  unit?: UnitOfMeasure;
}

const ManagePriceModal = (props: ManagePriceModalProps) => {
  const {handleClose, handleSaved, price: priceProps, unit} = props;

  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const [price, setPrice] = useState<ManagePrice>(priceProps || {});

  useEffect(() => {
    if (!priceProps) {
      setPrice({currency: supplierDetailsQuery.supplierCurrency});
    }
  }, [priceProps]);

  const errors = {
    unitCost: getFieldError(price.unitCost, FieldValidationType.REQUIRED_NUMBER),
  };

  return (
    <ModalComponent
      aria-label="over-drawer"
      handleClose={handleClose}
      title={t(`suppliers.details.catalog.manage.managePrice.title.${!priceProps ? 'add' : 'edit'}`)}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('suppliers.details.catalog.manage.puBuy')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
            handleChange={(unitCost) => setPrice((state) => ({
              ...state,
              unitCost,
              unitCostInCents: (unitCost || 0) * 100,
              wasUpdated: state.id != null
            }))}
            endAdornment={(
              <Typography fontSize={14} fontWeight="400">
                {price.currency}
              </Typography>
            )}
            value={price.unitCost ?? undefined}
            error={errors.unitCost}
            required/>
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('suppliers.details.catalog.manage.unit')}
            placeholder="-"
            value={unit || ''}
            items={UnitOfMeasure.selectItems()}
            readOnly/>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('suppliers.details.catalog.manage.minQuantity')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            handleChange={(minQuantity) => setPrice((state) => ({...state, minQuantity, wasUpdated: true}))}
            value={price.minQuantity ?? undefined}/>
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('suppliers.details.catalog.manage.maxQuantity')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
            handleChange={(maxQuantity) => setPrice((state) => ({...state, maxQuantity, wasUpdated: true}))}
            value={price.maxQuantity ?? undefined}/>
        </Stack>
      )}
      actions={(
        <>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t('global.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={loading || checkIfErrors(errors)}
            onClick={() => {
              setLoading(true);
              if (!priceProps?.id) {
                handleSaved(price);
                handleClose();
              } else {
                handleSaved(price);
                handleClose();
              }
            }}>
            {t('global.save')}
          </Button>
        </>
      )}/>
  );
};

export default ManagePriceModal;
